import { useState } from "react";
import { useViewer } from "../../../state/ViewerProvider";
import { notice } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";
import { takeShot, useCurrentShot } from "../basic/rail/ScenePanels";
import type { ChromeInsets } from "../layout";

const SHOTS = [
  { id: "hero", label: "Hero ¾" },
  { id: "front", label: "Front" },
  { id: "side", label: "Side" },
  { id: "top", label: "Top" },
];

/**
 * The floating view bar under the ring (Basic mode): the four shots every
 * product page uses, a slow spin and "fit". Worded, because an icon for
 * "front view" is a guess.
 *
 * Centred on the part of the canvas the panels leave visible — the panels float
 * over the canvas, so "centre of the canvas" would sit off to one side.
 */
export function ViewBar({ insets }: { insets: ChromeInsets }) {
  const { client } = useViewer();
  const r = client.ring;
  const shot = useCurrentShot();
  const [spin, setSpin] = useState(false);
  const fail = (e: Error) => notice(`Couldn't move the camera: ${e.message}`);
  const btn = "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-[13px] text-[13px] font-medium transition-colors";

  return (
    <div
      className="pointer-events-none absolute bottom-[26px] z-20 flex justify-center"
      style={{ left: insets.left, right: insets.right }}
    >
      <div className="pointer-events-auto mx-3 flex max-w-full items-center gap-0.5 overflow-x-auto rounded-xl border border-ed-border bg-ed-panel p-1 shadow-[0_6px_20px_rgba(0,0,0,0.08)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-view-bar>
        {SHOTS.map((s) => (
          <button
            key={s.id}
            onClick={() => void takeShot(r, s.id).catch(fail)}
            className={`${btn} shrink-0 ${shot === s.id ? "bg-ed-active text-ed-text" : "text-ed-text-2 hover:bg-ed-hover hover:text-ed-text"}`}
            aria-pressed={shot === s.id}
            data-view-shot={s.id}
          >
            {s.label}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-ed-border" />
        <button
          onClick={() => { const next = !spin; setSpin(next); void Promise.resolve(r.setAutoRotate(next, 0.6)).catch(fail); }}
          className={`${btn} ${spin ? "bg-ed-active text-ed-text" : "text-ed-text-2 hover:bg-ed-hover hover:text-ed-text"}`}
          aria-pressed={spin}
          data-view-spin
        >
          <Icon name="360" size={16} />
          Spin
        </button>
        <button
          onClick={() => void Promise.resolve(r.autoFit()).catch(fail)}
          className={`${btn} text-ed-text-2 hover:bg-ed-hover hover:text-ed-text`}
          data-view-fit
        >
          <Icon name="fit_screen" size={16} />
          Fit
        </button>
      </div>
    </div>
  );
}
